import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import {
  Mic, MicOff, Video, VideoOff, MonitorUp, PhoneOff, Bot, User, Send,
  Code2, Play, Check, ArrowRight, Loader2, Sparkles, Camera, Wifi, CheckCircle2,
} from "lucide-react";

const LANGS = ["python", "javascript", "java", "cpp", "sql"];
const LANG_LABEL = { python: "Python", javascript: "JavaScript", java: "Java", cpp: "C++", sql: "SQL" };

function CodingAssessment({ challenge, onSubmit }) {
  const [lang, setLang] = useState(challenge.starter?.python ? "python" : "python");
  const [code, setCode] = useState(challenge.starter?.python || "");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const changeLang = (l) => { setLang(l); setCode(challenge.starter?.[l] || ""); setResult(null); };

  const run = () => {
    setRunning(true);
    setResult(null);
    setTimeout(() => {
      const total = challenge.tests;
      const passed = Math.max(1, total - Math.floor(Math.random() * 2));
      setResult({ total, passed, ms: 40 + Math.floor(Math.random() * 260) });
      setRunning(false);
      toast[passed === total ? "success" : "message"](`${passed}/${total} test cases passed`);
    }, 1100);
  };

  const submit = () => {
    const r = result || { total: challenge.tests, passed: challenge.tests - 1, ms: 120 };
    setSubmitted(true);
    onSubmit({ passed: r.passed, total: r.total });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 h-full">
      {/* Problem */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 overflow-y-auto">
        <div className="flex items-center gap-2 mb-2">
          <span className="h-8 w-8 rounded-lg bg-brand-light grid place-items-center"><Code2 className="h-4 w-4 text-brand" /></span>
          <div className="font-heading font-bold text-ink">{challenge.title}</div>
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{challenge.difficulty}</span>
        </div>
        <p className="text-sm text-ink-soft leading-relaxed">{challenge.statement}</p>
        {challenge.examples?.map((ex, i) => (
          <div key={i} className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 num text-xs">
            <div className="text-ink-faint">Input: <span className="text-ink">{ex.input}</span></div>
            <div className="text-ink-faint mt-1">Output: <span className="text-ink">{ex.output}</span></div>
          </div>
        ))}
        <div className="mt-3">
          <div className="text-xs font-bold text-ink-faint uppercase tracking-wider mb-1">Constraints</div>
          <ul className="text-xs text-ink-soft space-y-1 num">{challenge.constraints?.map((c, i) => <li key={i}>· {c}</li>)}</ul>
        </div>
        {result && (
          <div className="mt-4 rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 bg-slate-900 text-white text-xs num flex justify-between">
              <span>Test Results</span><span>{result.ms} ms</span>
            </div>
            <div className="p-3 space-y-1.5">
              {Array.from({ length: result.total }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 text-xs num">
                  {i < result.passed ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <span className="h-3.5 w-3.5 rounded-full border-2 border-red-400" />}
                  <span className={i < result.passed ? "text-ink-soft" : "text-red-500"}>Test case {i + 1} {i < result.passed ? "passed" : "failed"}</span>
                </div>
              ))}
              <div className="pt-2 mt-1 border-t border-slate-100 text-xs font-bold flex justify-between">
                <span className={result.passed === result.total ? "text-emerald-600" : "text-amber-600"}>{result.passed}/{result.total} passed</span>
                <span className="text-brand num">Coding score {Math.round((result.passed / result.total) * 100)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Editor */}
      <div className="bg-slate-950 rounded-2xl border border-slate-800 flex flex-col overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-800">
          <select data-testid="code-language" value={lang} onChange={(e) => changeLang(e.target.value)}
            className="bg-slate-800 text-slate-200 text-xs font-semibold rounded-lg px-2.5 py-1.5 num focus:outline-none">
            {LANGS.map((l) => <option key={l} value={l}>{LANG_LABEL[l]}</option>)}
          </select>
          <div className="ml-auto flex gap-2">
            <button data-testid="run-code" onClick={run} disabled={running}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-900 bg-white hover:bg-slate-100 px-3 py-1.5 rounded-full transition-colors disabled:opacity-60">
              {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Run Code
            </button>
            <button data-testid="submit-code" onClick={submit} disabled={submitted}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand hover:bg-brand-hover px-3 py-1.5 rounded-full transition-colors disabled:opacity-60">
              <Check className="h-3.5 w-3.5" /> {submitted ? "Submitted" : "Submit"}
            </button>
          </div>
        </div>
        <textarea data-testid="code-editor" value={code} onChange={(e) => setCode(e.target.value)} spellCheck={false}
          className="flex-1 bg-slate-950 text-slate-100 num text-[13px] leading-6 p-4 resize-none focus:outline-none min-h-[280px]" />
      </div>
    </div>
  );
}

function PreInterview({ iv, onStart, onCancel }) {
  return (
    <div className="min-h-screen bg-slate-950 text-white grid place-items-center px-5 py-10">
      <div className="w-full max-w-lg bg-slate-900 rounded-3xl border border-white/10 p-8 fade-up">
        <div className="h-14 w-14 rounded-2xl bg-brand/20 grid place-items-center mb-5 ring-2 ring-brand/30"><Video className="h-7 w-7 text-brand" /></div>
        <h1 className="font-heading text-2xl font-bold">Ready for your AI Video Interview?</h1>
        <p className="text-slate-400 text-sm mt-1">A live AI interviewer will ask questions and evaluate your responses.</p>
        <div className="mt-6 rounded-2xl border border-white/10 divide-y divide-white/5">
          {[
            ["Interview", iv.role_name],
            ["Interview Type", iv.mode_label || "AI Video Interview"],
            ["Duration", `${iv.duration} minutes`],
            ["Panel", iv.panel_name],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between px-4 py-3 text-sm"><span className="text-slate-400">{k}</span><span className="font-semibold">{v}</span></div>
          ))}
        </div>
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Requirements</div>
          <div className="flex flex-wrap gap-3 text-sm">
            {["Camera", "Microphone", "Stable internet connection"].map((r) => (
              <span key={r} className="inline-flex items-center gap-1.5 text-slate-300"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> {r}</span>
            ))}
          </div>
        </div>
        <div className="mt-7 flex gap-3">
          <button data-testid="test-devices" onClick={onStart} className="flex-1 text-sm font-bold text-ink bg-white hover:bg-slate-100 px-5 py-3 rounded-full transition-colors">Test Camera & Microphone</button>
          <button data-testid="start-video-interview" onClick={onStart} className="flex-1 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full transition-colors">Start Interview</button>
        </div>
        <button onClick={onCancel} className="mt-3 w-full text-xs font-semibold text-slate-500 hover:text-slate-300">Cancel</button>
      </div>
    </div>
  );
}

function DeviceCheck({ onContinue, onBack }) {
  const [ready, setReady] = useState({ cam: false, mic: false, net: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        stream.getTracks().forEach((t) => t.stop());
      } catch (err) { console.debug("Media permissions unavailable, simulating device readiness", err); }
      const t1 = setTimeout(() => !cancelled && setReady((s) => ({ ...s, cam: true })), 600);
      const t2 = setTimeout(() => !cancelled && setReady((s) => ({ ...s, mic: true })), 1100);
      const t3 = setTimeout(() => !cancelled && setReady((s) => ({ ...s, net: true })), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    })();
    return () => { cancelled = true; };
  }, []);
  const allReady = ready.cam && ready.mic && ready.net;
  const rows = [
    ["Camera", ready.cam, Camera], ["Microphone", ready.mic, Mic], ["Internet", ready.net, Wifi],
  ];
  return (
    <div className="min-h-screen bg-slate-950 text-white grid place-items-center px-5 py-10">
      <div className="w-full max-w-md bg-slate-900 rounded-3xl border border-white/10 p-8 fade-up">
        <h1 className="font-heading text-2xl font-bold">Device Check</h1>
        <p className="text-slate-400 text-sm mt-1">We're confirming your camera, microphone and connection.</p>
        <div className="mt-6 aspect-video rounded-2xl bg-slate-800 border border-white/10 grid place-items-center relative overflow-hidden">
          <User className="h-14 w-14 text-slate-600" />
          <span className="absolute top-3 left-3 text-[11px] font-semibold text-white/70">Camera preview</span>
          {ready.cam && <span className="absolute top-3 right-3 text-[11px] font-bold text-emerald-400 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> live</span>}
        </div>
        <div className="mt-5 space-y-2.5" data-testid="device-status">
          {rows.map(([label, ok, Icon]) => (
            <div key={label} className="flex items-center justify-between rounded-xl border border-white/10 px-4 py-2.5">
              <span className="inline-flex items-center gap-2 text-sm text-slate-200"><Icon className="h-4 w-4 text-slate-400" /> {label}</span>
              {ok ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-400"><Check className="h-3.5 w-3.5" /> Ready</span>
                : <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
            </div>
          ))}
        </div>
        <button data-testid="continue-to-interview" onClick={onContinue} disabled={!allReady}
          className="mt-6 w-full text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full transition-colors disabled:opacity-40">
          Continue to Interview
        </button>
        <button onClick={onBack} className="mt-3 w-full text-xs font-semibold text-slate-500 hover:text-slate-300">Back</button>
      </div>
    </div>
  );
}

export default function InterviewRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [iv, setIv] = useState(null);
  const [idx, setIdx] = useState(0);
  const [transcript, setTranscript] = useState([]);
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [coding, setCoding] = useState(null); // {passed,total}
  const [analyzing, setAnalyzing] = useState(false);
  const [stage, setStage] = useState("pre"); // pre | devicecheck | live
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/interviews/${id}`).then((r) => {
      if (r.data.status === "completed") { navigate(`/candidate/result/${id}`); return; }
      if (r.data.mode === "voice") { navigate(`/candidate/voice/${id}`, { replace: true }); return; }
      setIv(r.data);
    }).catch(() => { toast.error("Interview not found"); navigate("/candidate"); });
  }, [id, navigate]);

  useEffect(() => {
    if (!iv) return;
    const q = iv.questions[idx];
    setTranscript((t) => [...t, { who: "interviewer", text: q.question, phase: q.phase }]);
  }, [iv, idx]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9, behavior: "smooth" }); }, [transcript]);

  if (!iv) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  if (stage === "pre") return <PreInterview iv={iv} onStart={() => setStage("devicecheck")} onCancel={() => navigate("/candidate")} />;
  if (stage === "devicecheck") return <DeviceCheck onContinue={() => setStage("live")} onBack={() => setStage("pre")} />;

  const q = iv.questions[idx];
  const total = iv.questions.length;
  const isCoding = q.type === "coding" && iv.coding_challenge;
  const isLast = idx === total - 1;

  const answer = () => setTranscript((t) => [...t, { who: "candidate", text: `(Answered: ${q.phase})` }]);

  const next = () => {
    answer();
    if (isLast) return end();
    setIdx(idx + 1);
  };

  const end = async () => {
    setAnalyzing(true);
    try {
      await new Promise((r) => setTimeout(r, 2400));
      await api.post(`/interviews/${id}/complete`, {
        coding_language: "python",
        coding_passed: coding?.passed ?? null,
        coding_total: coding?.total ?? null,
      });
      navigate(`/candidate/result/${id}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setAnalyzing(false);
    }
  };

  if (analyzing) {
    return (
      <div className="fixed inset-0 bg-slate-950 text-white grid place-items-center z-50">
        <div className="text-center">
          <div className="relative h-20 w-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-white/10" />
            <div className="absolute inset-0 rounded-full border-4 border-brand border-t-transparent animate-spin" />
            <Sparkles className="absolute inset-0 m-auto h-7 w-7 text-brand" />
          </div>
          <div className="font-heading text-2xl font-bold">Analyzing Your Interview…</div>
          <p className="text-slate-400 text-sm mt-2">Scoring competencies, coding and role fit.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-100 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 h-16 flex items-center justify-between shrink-0">
        <div>
          <div className="font-heading font-bold text-ink text-sm">{iv.role_name} · {iv.mode_label || "AI Video Interview"}</div>
          <div className="text-xs text-ink-faint num">Question {idx + 1} of {total} · {q.phase}</div>
        </div>
        <div className="flex-1 max-w-xs mx-6 hidden sm:block">
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
          </div>
        </div>
        <button data-testid="end-interview" onClick={end}
          className="inline-flex items-center gap-2 text-xs font-bold text-white bg-red-500 hover:bg-red-600 px-4 py-2 rounded-full transition-colors">
          <PhoneOff className="h-3.5 w-3.5" /> End Interview
        </button>
      </div>

      <div className="flex-1 min-h-0 p-4 sm:p-5">
        {isCoding ? (
          <div className="h-full flex flex-col gap-4">
            <div className="flex-1 min-h-0"><CodingAssessment challenge={iv.coding_challenge} onSubmit={(r) => { setCoding(r); }} /></div>
            <div className="flex justify-end shrink-0">
              <button data-testid="coding-continue" onClick={next}
                className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-6 py-2.5 rounded-full transition-colors">
                {isLast ? "Finish Interview" : "Continue Interview"} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="h-full grid lg:grid-cols-3 gap-4">
            {/* Video area */}
            <div className="lg:col-span-2 bg-slate-900 rounded-2xl relative overflow-hidden flex items-center justify-center">
              <div className="text-center">
                <div className="h-24 w-24 rounded-full bg-brand/20 grid place-items-center mx-auto mb-3 ring-4 ring-brand/30">
                  {iv.type === "ai" ? <Bot className="h-12 w-12 text-brand" /> : <User className="h-12 w-12 text-brand" />}
                </div>
                <div className="text-white font-semibold">{iv.type === "ai" ? "Mock2Crack AI Interviewer" : iv.panel_name}</div>
                <div className="text-slate-400 text-xs mt-0.5">Listening…</div>
              </div>
              {/* candidate PIP */}
              <div className="absolute bottom-4 right-4 h-28 w-40 rounded-xl bg-slate-800 border border-white/10 grid place-items-center">
                {cam ? <User className="h-8 w-8 text-slate-400" /> : <VideoOff className="h-6 w-6 text-slate-500" />}
                <span className="absolute bottom-1.5 left-2 text-[10px] text-white/70 font-semibold">You</span>
              </div>
              {/* controls */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <button onClick={() => setMic(!mic)} data-testid="toggle-mic" className={`h-11 w-11 rounded-full grid place-items-center transition-colors ${mic ? "bg-white/10 text-white hover:bg-white/20" : "bg-red-500 text-white"}`}>
                  {mic ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                </button>
                <button onClick={() => setCam(!cam)} data-testid="toggle-cam" className={`h-11 w-11 rounded-full grid place-items-center transition-colors ${cam ? "bg-white/10 text-white hover:bg-white/20" : "bg-red-500 text-white"}`}>
                  {cam ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                </button>
                <button data-testid="screen-share" className="h-11 w-11 rounded-full bg-white/10 text-white grid place-items-center hover:bg-white/20 transition-colors">
                  <MonitorUp className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Transcript + question */}
            <div className="bg-white rounded-2xl border border-slate-200 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 font-heading font-bold text-ink text-sm">Live Transcript</div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {transcript.map((m, i) => (
                  <div key={i} className={`flex ${m.who === "candidate" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.who === "candidate" ? "bg-brand text-white" : "bg-slate-100 text-ink"}`}>
                      {m.phase && <div className="text-[10px] font-bold uppercase tracking-wider opacity-60 mb-0.5">{m.phase}</div>}
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-slate-200 space-y-2">
                <button onClick={answer} data-testid="answer-question" className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors">
                  <Send className="h-3.5 w-3.5" /> Submit Answer
                </button>
                <button onClick={next} data-testid="next-question" className="w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2.5 rounded-full transition-colors">
                  {isLast ? "Finish Interview" : "Next Question"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
